import React, { useState, useEffect, useCallback } from 'react';
import { useDataFetch } from '../../hooks/useApi';
import { useTheme } from '../../contexts/ThemeContext';
import { useToast } from '../../contexts/ToastContext.jsx';
import AuditLogTable from './AuditLogTable';
import FilterModule from '../../modules/filter/FilterModule.jsx';
import { ModuleContextProvider } from '../../modules/context/ModuleContextProvider.jsx';
import AuditLogExport from './AuditLogExport';

/**
 * Main audit log viewer component with advanced filtering
 */
const AuditLogViewer = () => {
  const { data: auditData, loading, error, fetchData, refetch } = useDataFetch();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    userId: '',
    adminId: '',
    operationType: '',
    resourceType: '',
    status: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    sortBy: 'created_at',
    sortOrder: 'desc',
    total: 0,
    totalPages: 0
  });

  /**
   * Fetch audit logs with current filters and pagination
   */
  const fetchAuditLogs = useCallback(async () => {
    const queryParams = new URLSearchParams();
    
    // Add pagination
    queryParams.append('page', pagination.page);
    queryParams.append('limit', pagination.limit);
    queryParams.append('sortBy', pagination.sortBy);
    queryParams.append('sortOrder', pagination.sortOrder);
    
    // Add filters with proper parameter mapping
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value.trim()) {
        // Map frontend filter names to backend parameter names
        const paramMap = {
          'operationType': 'operation_type',
          'resourceType': 'resource_type',
          'userId': 'user_id',
          'adminId': 'admin_id',
          'startDate': 'start_date',
          'endDate': 'end_date'
        };
        const paramName = paramMap[key] || key;
        queryParams.append(paramName, value.trim());
      }
    });

    await fetchData(
      (api) => api.get(`/api/admin/audit?${queryParams.toString()}`),
      {
        showErrorToast: true,
        onSuccess: (data) => {
          setPagination(prev => ({
            ...prev,
            total: data.pagination?.total || 0,
            totalPages: data.pagination?.total_pages || 0
          }));
        }
      }
    );
  }, [fetchData, filters, pagination.page, pagination.limit, pagination.sortBy, pagination.sortOrder]);

  /**
   * Handle filter changes from FilterModule
   */
  const handleFiltersChange = useCallback((activeFilters) => {
    // Convert FilterModule activeFilters to legacy filter format
    const newFilters = {
      startDate: '',
      endDate: '',
      userId: '',
      adminId: '',
      operationType: '',
      resourceType: '',
      status: '',
      search: ''
    };

    activeFilters.forEach(filter => {
      switch (filter.id) {
        case 'search':
          newFilters.search = filter.value;
          break;
        case 'operation_type':
          newFilters.operationType = filter.value;
          break;
        case 'resource_type':
          newFilters.resourceType = filter.value;
          break;
        case 'status':
          newFilters.status = filter.value;
          break;
        case 'user_id':
          newFilters.userId = filter.value;
          break;
        case 'admin_id':
          newFilters.adminId = filter.value;
          break;
        case 'date_range':
          if (filter.operator.type === 'between' && Array.isArray(filter.value)) {
            newFilters.startDate = filter.value[0];
            newFilters.endDate = filter.value[1];
          } else if (filter.operator.type === 'greater-than') {
            newFilters.startDate = filter.value;
          } else if (filter.operator.type === 'less-than') {
            newFilters.endDate = filter.value;
          }
          break;
        default:
          break;
      }
    });

    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  /**
   * Handle legacy filter changes (for backward compatibility)
   */
  const handleLegacyFilterChange = useCallback((newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
    setPagination(prev => ({ ...prev, page: 1 })); // Reset to first page
  }, []);

  /**
   * Handle pagination changes
   */
  const handlePageChange = useCallback((newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  }, []);

  /**
   * Handle sorting changes
   */
  const handleSort = useCallback((sortBy, sortOrder) => {
    setPagination(prev => ({
      ...prev,
      sortBy,
      sortOrder,
      page: 1 // Reset to first page when sorting
    }));
  }, []);

  /**
   * Handle refresh
   */
  const handleRefresh = useCallback(() => {
    refetch((api) => {
      const queryParams = new URLSearchParams();
      
      queryParams.append('page', pagination.page);
      queryParams.append('limit', pagination.limit);
      queryParams.append('sortBy', pagination.sortBy);
      queryParams.append('sortOrder', pagination.sortOrder);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value.trim()) {
          // Map frontend filter names to backend parameter names
          const paramMap = {
            'operationType': 'operation_type',
            'resourceType': 'resource_type',
            'userId': 'user_id',
            'adminId': 'admin_id',
            'startDate': 'start_date',
            'endDate': 'end_date'
          };
          const paramName = paramMap[key] || key;
          queryParams.append(paramName, value.trim());
        }
      });

      return api.get(`/api/admin/audit?${queryParams.toString()}`);
    });
  }, [refetch, filters, pagination]);

  /**
   * Clear all filters
   */
  const handleClearFilters = useCallback(() => {
    const clearedFilters = {
      startDate: '',
      endDate: '',
      userId: '',
      adminId: '',
      operationType: '',
      resourceType: '',
      status: '',
      search: ''
    };
    setFilters(clearedFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    return clearedFilters;
  }, []);

  // Fetch data when filters or pagination change
  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);



  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Audit Logs</h1>
          <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
            Monitor and review all administrative actions and system events
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className={`px-4 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${
                  theme === 'dark' ? 'border-gray-300' : 'border-gray-700'
                }`}></div>
                <span>Refreshing...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-2">
                <span>🔄</span>
                <span>Refresh</span>
              </div>
            )}
          </button>
          
          <AuditLogExport 
            filters={filters}
            disabled={loading || !auditData?.audit_logs?.length}
          />
        </div>
      </div>

      {/* Filters */}
      <ModuleContextProvider
        user={{ id: 'admin', permissions: ['admin'] }}
        permissions={[{ id: 'admin', name: 'Admin Access' }]}
        apiClient={{ get: () => {}, post: () => {}, put: () => {}, delete: () => {} }}
        theme={{ mode: theme }}
        toast={{ success: showToast, error: showToast, warning: showToast, info: showToast }}
      >
        <FilterModule
          config={{
            context: 'admin-audit',
            filters: [], // Will use context-specific defaults
            layout: 'horizontal',
            persistFilters: true,
            storageKey: 'admin-audit-filters',
            showClearAll: true,
            realTimeFiltering: true,
            debounceMs: 500,
            onFilterChange: handleFiltersChange,
            onFilterReset: handleClearFilters
          }}
          className="mb-6"
          testId="admin-audit-filter"
        />
      </ModuleContextProvider>

      {/* Error State */}
      {error && (
        <div className={`rounded-lg p-4 border ${
          theme === 'dark'
            ? 'bg-red-900/20 border-red-500/30'
            : 'bg-red-50 border-red-200'
        }`}>
          <div className="flex items-center space-x-2">
            <span className={theme === 'dark' ? 'text-red-400' : 'text-red-600'}>⚠️</span>
            <span className={`font-medium ${theme === 'dark' ? 'text-red-400' : 'text-red-800'}`}>Error loading audit logs</span>
          </div>
          <p className={`mt-1 ${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
          <button
            onClick={handleRefresh}
            className={`mt-3 px-3 py-1 rounded transition-colors ${
              theme === 'dark'
                ? 'bg-red-800 text-red-200 hover:bg-red-700'
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
          >
            Try Again
          </button>
        </div>
      )}

      {/* Debug Info */}
      {process.env.NODE_ENV === 'development' && (
        <div className={`p-4 rounded-lg border text-sm ${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700 text-gray-300'
            : 'bg-gray-50 border-gray-200 text-gray-700'
        }`}>
          <h4 className="font-medium mb-2">Debug Info:</h4>
          <div>Loading: {loading ? 'true' : 'false'}</div>
          <div>Error: {error || 'none'}</div>
          <div>Data: {auditData ? 'present' : 'null'}</div>
          <div>Audit Logs Count: {auditData?.audit_logs?.length || 0}</div>
          <div>Total: {auditData?.pagination?.total || 0}</div>
        </div>
      )}

      {/* Audit Log Table */}
      <AuditLogTable
        logs={auditData?.audit_logs || []}
        loading={loading}
        pagination={{
          ...pagination,
          total: auditData?.pagination?.total || 0,
          totalPages: auditData?.pagination?.total_pages || 0
        }}
        onPageChange={handlePageChange}
        onSort={handleSort}
        theme={theme}
      />



      {/* Summary Stats */}
      {auditData?.pagination && (
        <div className={`rounded-lg border p-4 ${
          theme === 'dark'
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}>
          <h3 className={`text-lg font-medium mb-3 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>Summary Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-teal-400">{auditData.pagination.total || 0}</div>
              <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Entries</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">{auditData.pagination.page || 1}</div>
              <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Current Page</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-400">{auditData.pagination.total_pages || 0}</div>
              <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Total Pages</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-400">{auditData.pagination.limit || 50}</div>
              <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Per Page</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditLogViewer;
